import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getApiUrl } from '../utils/config';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  ChevronDown, ChevronUp, Zap, Play, CheckCircle2, Plus, Edit2, Trash2,
  Clock, ListTodo, X, Flag, Sparkles, RefreshCw, Loader2, Check,
} from 'lucide-react';
import SprintModal from './SprintModal';

const fmt = d => d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null;
const fmtPlanDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

const DAY_MS = 24 * 60 * 60 * 1000;
const daysLeft = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate).getTime() - Date.now()) / DAY_MS);
};

const STATUS_META = {
  todo: { label: 'To Do', dot: 'bg-slate-400' },
  in_progress: { label: 'In Progress', dot: 'bg-blue-500' },
  in_review: { label: 'In Review', dot: 'bg-amber-500' },
  approved: { label: 'Done', dot: 'bg-emerald-500' },
  done: { label: 'Done', dot: 'bg-emerald-500' },
};

const SPRINT_BADGE = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  planning: 'bg-slate-100 text-slate-600 border-slate-200',
  completed: 'bg-[#ede0ff] text-[#4B1E87] border-[#875AC3]/30',
};

const memberName = (members, id) => members.find(m => m.user_id === id || m.id === id)?.full_name || null;

/* ── AI plan vertical timeline with checkable steps ──────────── */
const TaskPlanTimeline = ({ plan, planLoading, planError, onRegenerate, regenerating, onToggleStep, checkedSteps, savingStep }) => {
  if (planLoading) {
    return (
      <div className="flex items-center gap-2 py-4 px-3 text-xs text-slate-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#5F2AA8]" />
        AI is planning this task…
      </div>
    );
  }
  if (planError) {
    return <p className="text-xs text-rose-500 py-3 px-3">{planError}</p>;
  }
  if (!plan) return null;
  const checked = new Set(checkedSteps || []);
  const firstUnchecked = plan.steps.findIndex((_, i) => !checked.has(i));
  return (
    <div className="px-3 pb-3 pt-1">
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1 text-[11px] font-semibold text-[#4B1E87]">
          <Sparkles className="w-3 h-3" /> AI Plan
          <span className="font-normal text-slate-400">· {fmtPlanDate(plan.window_start)} → {fmtPlanDate(plan.window_end)}</span>
          {plan.mock && <span className="font-normal text-amber-500" title="No AI key configured — showing a template plan">(sample)</span>}
        </span>
        <button
          onClick={onRegenerate}
          disabled={regenerating}
          className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-[#4B1E87] transition-colors"
          title="Regenerate plan"
        >
          <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} /> Regenerate
        </button>
      </div>
      {plan.summary && <p className="text-[11px] text-slate-500 italic mb-2">{plan.summary}</p>}
      {/* Vertical dot-to-dot timeline; tick each step as you finish it */}
      <div className="relative pl-6">
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gradient-to-b from-[#875AC3] to-[#4B1E87]/30" />
        {plan.steps.map((step, i) => {
          const done = checked.has(i);
          const current = !done && i === firstUnchecked;
          return (
            <div key={i} className="relative pb-3 last:pb-0">
              <button
                onClick={() => onToggleStep(i)}
                disabled={savingStep}
                title={done ? 'Mark step as not done' : 'Mark step as done'}
                className={`absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full border-2 flex items-center justify-center transition-colors ${
                  done ? 'bg-[#4B1E87] border-[#4B1E87] hover:bg-[#3a1569]' :
                  current ? 'bg-white border-[#4B1E87] ring-2 ring-[#875AC3]/30 hover:bg-[#f3eeff]' :
                  'bg-white border-slate-300 hover:border-[#875AC3]'
                }`}
              >
                {done && <Check className="w-3 h-3 text-white" />}
                {current && <span className="w-2 h-2 rounded-full bg-[#4B1E87]" />}
              </button>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`text-xs font-medium ${done ? 'line-through text-slate-400' : current ? 'text-[#4B1E87]' : 'text-slate-700'}`}>{step.title}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">
                  {fmtPlanDate(step.start_date)} → {fmtPlanDate(step.end_date)}
                </span>
                {current && <span className="text-[9px] font-semibold text-white bg-[#4B1E87] rounded-full px-1.5 py-px">Now</span>}
              </div>
              {step.description && <p className={`text-[11px] mt-0.5 leading-snug ${done ? 'text-slate-400' : 'text-slate-500'}`}>{step.description}</p>}
            </div>
          );
        })}
      </div>
      {plan.steps.length > 0 && checked.size === plan.steps.length && (
        <p className="flex items-center gap-1 mt-2 text-[11px] font-medium text-emerald-600">
          <CheckCircle2 className="w-3.5 h-3.5" /> All steps done — task marked complete.
        </p>
      )}
    </div>
  );
};

/* ── Sprint task row ─────────────────────────────────────────── */
const SprintTaskRow = ({ task, members, canManage, onRemove, removing, selected, onSelect, planState, onRegeneratePlan, onToggleStep, savingStep }) => {
  const meta = STATUS_META[task.status] || STATUS_META.todo;
  const assignee = memberName(members, task.assignee_id);
  return (
    <div className={`rounded-lg border transition-colors ${selected ? 'bg-[#faf7ff] border-[#875AC3]/40' : 'bg-white/70 border-slate-100'}`}>
      <div className="flex items-center gap-2 px-2.5 py-1.5 group cursor-pointer" onClick={() => onSelect(task.id)}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} title={meta.label} />
        <span className={`text-xs flex-1 truncate ${task.status === 'approved' || task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-700'}`}>
          {task.title}
        </span>
        <Sparkles className={`w-3 h-3 shrink-0 ${selected ? 'text-[#4B1E87]' : 'text-slate-300 group-hover:text-[#875AC3]'}`} title="View AI plan" />
        {assignee && (
          <span className="w-5 h-5 rounded-full bg-[#ede0ff] text-[#4B1E87] text-[9px] font-bold flex items-center justify-center shrink-0" title={assignee}>
            {assignee.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="text-[10px] text-slate-400 shrink-0 hidden sm:inline">{meta.label}</span>
        {canManage && onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(task.id); }}
            disabled={removing}
            title="Remove from sprint"
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-500 shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {selected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-[#875AC3]/20"
          >
            <TaskPlanTimeline
              plan={planState?.plan}
              planLoading={planState?.loading}
              planError={planState?.error}
              onRegenerate={() => onRegeneratePlan(task.id)}
              regenerating={planState?.loading}
              onToggleStep={(stepIdx) => onToggleStep(task.id, stepIdx)}
              checkedSteps={planState?.checkedSteps}
              savingStep={savingStep}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Main panel ──────────────────────────────────────────────── */
const SprintPanel = ({
  sprints = [],
  tasks = [],
  members = [],
  orgId,
  projectId,
  canManage,
  onCreateSprint,
  onUpdateSprint,
  onDeleteSprint,
  onStartSprint,
  onCompleteSprint,
  onAddTasksToSprint,
  onRemoveTaskFromSprint,
  onRefresh,
  loading,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingStep, setSavingStep] = useState(false);
  const [expandedSprints, setExpandedSprints] = useState({}); // sprintId -> bool
  const [showBacklogPicker, setShowBacklogPicker] = useState(null); // sprintId or null
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskPlans, setTaskPlans] = useState({}); // taskId -> { plan, loading, error, checkedSteps }

  const activeSprint = sprints.find(s => s.status === 'active');
  const orderedSprints = [
    ...sprints.filter(s => s.status === 'active'),
    ...sprints.filter(s => s.status === 'planning'),
    ...sprints.filter(s => s.status === 'completed'),
  ];

  const sprintTasks = sprint => tasks.filter(t => t.sprint_id === sprint.id);
  const doneTasks = sprint => sprintTasks(sprint).filter(t => t.status === 'approved' || t.status === 'done').length;
  const backlogTasks = tasks.filter(t => !t.sprint_id && t.status !== 'approved' && t.status !== 'done');

  const isExpanded = (s) => expandedSprints[s.id] ?? s.status === 'active';
  const toggleExpanded = (s) => setExpandedSprints(p => ({ ...p, [s.id]: !isExpanded(s) }));

  const fetchTaskPlan = async (taskId, force = false) => {
    setTaskPlans(p => ({ ...p, [taskId]: { ...p[taskId], loading: true, error: null } }));
    try {
      const res = await axios.post(
        getApiUrl(`/organizations/${orgId}/tasks/${taskId}/ai-plan${force ? '?force_regenerate=true' : ''}`)
      );
      setTaskPlans(p => ({
        ...p,
        [taskId]: {
          plan: res.data,
          loading: false,
          error: null,
          checkedSteps: force ? [] : (res.data.checked_steps || []),
        },
      }));
    } catch (e) {
      setTaskPlans(p => ({
        ...p,
        [taskId]: { plan: null, loading: false, error: 'Could not generate a plan for this task. Try again.', checkedSteps: [] },
      }));
    }
  };

  const handleSelectTask = (taskId) => {
    if (selectedTaskId === taskId) { setSelectedTaskId(null); return; }
    setSelectedTaskId(taskId);
    if (!taskPlans[taskId]?.plan && !taskPlans[taskId]?.loading) fetchTaskPlan(taskId);
  };

  const handleToggleStep = async (taskId, stepIdx) => {
    const state = taskPlans[taskId];
    if (!state?.plan) return;
    const total = state.plan.steps.length;
    const prev = state.checkedSteps || [];
    const next = prev.includes(stepIdx) ? prev.filter(i => i !== stepIdx) : [...prev, stepIdx].sort((a, b) => a - b);
    // Optimistic update
    setTaskPlans(p => ({ ...p, [taskId]: { ...p[taskId], checkedSteps: next } }));
    setSavingStep(true);
    try {
      const res = await axios.put(
        getApiUrl(`/organizations/${orgId}/tasks/${taskId}/ai-plan/progress`),
        { checked_steps: next, total_steps: total }
      );
      // Backend may have completed the task and/or the whole sprint
      if ((res.data.task_completed || res.data.sprint_completed) && onRefresh) {
        onRefresh();
      }
    } catch (e) {
      // Revert on failure
      setTaskPlans(p => ({ ...p, [taskId]: { ...p[taskId], checkedSteps: prev } }));
    } finally {
      setSavingStep(false);
    }
  };

  const handleSave = async ({ selectedTaskIds = [], ...data }) => {
    setSaving(true);
    try {
      if (editingSprint) {
        await onUpdateSprint(editingSprint.id, data);
      } else {
        const newSprint = await onCreateSprint(data);
        // Assign selected tasks to the new sprint
        if (selectedTaskIds.length > 0 && newSprint?.id && onAddTasksToSprint) {
          await onAddTasksToSprint(newSprint.id, selectedTaskIds);
        }
      }
      setShowModal(false);
      setEditingSprint(null);
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => { setEditingSprint(null); setShowModal(true); };
  const openEdit = (s) => { setEditingSprint(s); setShowModal(true); };

  const handleRemoveTask = async (sprintId, taskId) => {
    if (!onRemoveTaskFromSprint) return;
    setSaving(true);
    try { await onRemoveTaskFromSprint(sprintId, taskId); } finally { setSaving(false); }
  };

  const handleAddBacklogTask = async (sprintId, taskId) => {
    if (!onAddTasksToSprint) return;
    setSaving(true);
    try { await onAddTasksToSprint(sprintId, [taskId]); } finally { setSaving(false); }
  };

  return (
    <>
      <div className="mb-4 rounded-2xl border border-slate-200/70 bg-white/80 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <button
            className="flex items-center gap-2 font-semibold text-sm hover:text-[#4B1E87] transition-colors"
            onClick={() => setCollapsed(c => !c)}
          >
            <Zap className="w-4 h-4 text-[#5F2AA8]" />
            Sprints
            <span className="text-xs font-normal text-slate-400">({sprints.length})</span>
            {activeSprint && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">1 Active</Badge>}
            {collapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
          </button>
          {canManage && (
            <Button size="sm" variant="outline" onClick={openCreate} className="h-7 text-xs gap-1">
              <Plus className="w-3 h-3" /> New Sprint
            </Button>
          )}
        </div>

        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-3">
                {orderedSprints.map(sprint => {
                  const isActive = sprint.status === 'active';
                  const isPlanning = sprint.status === 'planning';
                  const expanded = isExpanded(sprint);
                  const total = sprintTasks(sprint).length;
                  const done = doneTasks(sprint);
                  const remaining = isActive ? daysLeft(sprint.end_date) : null;
                  return (
                    <div
                      key={sprint.id}
                      className={`rounded-xl border ${
                        isActive ? 'border-emerald-200 bg-emerald-50/60' :
                        isPlanning ? 'border-slate-200 bg-slate-50/40' :
                        'border-slate-100 bg-slate-50/30'
                      }`}
                    >
                      {/* Sprint row */}
                      <div className="flex items-start justify-between gap-2 p-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{sprint.name}</span>
                            <Badge className={`text-[10px] ${SPRINT_BADGE[sprint.status] || SPRINT_BADGE.planning}`}>
                              {sprint.status === 'active' ? 'Active' : sprint.status === 'planning' ? 'Planning' : 'Completed'}
                            </Badge>
                            {remaining !== null && (
                              <span className={`flex items-center gap-1 text-[11px] font-medium ${remaining < 0 ? 'text-rose-600' : remaining <= 2 ? 'text-amber-600' : 'text-emerald-700'}`}>
                                <Clock className="w-3 h-3" />
                                {remaining < 0 ? `${Math.abs(remaining)}d overdue` : remaining === 0 ? 'Ends today' : `${remaining}d left`}
                              </span>
                            )}
                          </div>
                          {sprint.goal && (
                            <p className="flex items-start gap-1 text-xs text-slate-600 mt-1">
                              <Flag className="w-3 h-3 mt-0.5 shrink-0 text-[#5F2AA8]" />
                              <span className="italic">{sprint.goal}</span>
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {(sprint.start_date || sprint.end_date) && <>{fmt(sprint.start_date)} → {fmt(sprint.end_date)} · </>}
                            {done} / {total} task{total !== 1 ? 's' : ''} done
                          </p>
                          {/* Progress */}
                          {total > 0 && (
                            <div className="mt-2 flex items-center gap-2">
                              <Progress value={(done / total) * 100} className="h-1.5 flex-1" />
                              <span className="text-[10px] text-slate-400 shrink-0">{Math.round((done / total) * 100)}%</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {canManage && isActive && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEdit(sprint)} className="h-6 px-2 text-xs">
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={async () => { setSaving(true); try { await onCompleteSprint(sprint.id); } finally { setSaving(false); } }}
                                disabled={saving}
                                className="h-6 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                              </Button>
                            </>
                          )}
                          {canManage && isPlanning && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEdit(sprint)} className="h-6 px-2 text-xs">
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={async () => { setSaving(true); try { await onStartSprint(sprint.id); } finally { setSaving(false); } }}
                                disabled={saving || !!activeSprint}
                                title={activeSprint ? 'Complete the active sprint first' : ''}
                                className="h-6 px-2 text-xs"
                              >
                                <Play className="w-3 h-3 mr-1" /> Start
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={async () => { setSaving(true); try { await onDeleteSprint(sprint.id); } finally { setSaving(false); } }}
                                disabled={saving}
                                className="h-6 px-2 text-xs text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          {/* Expand / collapse sprint plan */}
                          <button
                            onClick={() => toggleExpanded(sprint)}
                            title={expanded ? 'Hide sprint plan' : 'Show sprint plan'}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#4B1E87] hover:bg-white/70 transition-colors"
                          >
                            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded: sprint plan (task list + AI plans) */}
                      <AnimatePresence initial={false}>
                        {expanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-3 pb-3 space-y-1">
                              <div className="flex items-center justify-between pt-1">
                                <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                  <ListTodo className="w-3 h-3" /> Sprint tasks
                                </span>
                                {canManage && (isActive || isPlanning) && backlogTasks.length > 0 && (
                                  <button
                                    onClick={() => setShowBacklogPicker(v => v === sprint.id ? null : sprint.id)}
                                    className="flex items-center gap-1 text-[11px] font-medium text-[#4B1E87] hover:underline"
                                  >
                                    <Plus className="w-3 h-3" /> Add from backlog
                                  </button>
                                )}
                              </div>
                              {sprintTasks(sprint).length === 0 ? (
                                <p className="text-xs text-muted-foreground py-1">No tasks in this sprint.</p>
                              ) : (
                                <div className="space-y-1">
                                  {sprintTasks(sprint).map(t => (
                                    <SprintTaskRow
                                      key={t.id}
                                      task={t}
                                      members={members}
                                      canManage={canManage && (isActive || isPlanning)}
                                      onRemove={(taskId) => handleRemoveTask(sprint.id, taskId)}
                                      removing={saving}
                                      selected={selectedTaskId === t.id}
                                      onSelect={handleSelectTask}
                                      planState={taskPlans[t.id]}
                                      onRegeneratePlan={(id) => fetchTaskPlan(id, true)}
                                      onToggleStep={handleToggleStep}
                                      savingStep={savingStep}
                                    />
                                  ))}
                                </div>
                              )}
                              {/* Backlog picker */}
                              {showBacklogPicker === sprint.id && canManage && (
                                <div className="mt-2 rounded-lg border border-dashed border-[#875AC3]/40 bg-[#f7f2ff] p-2 space-y-1">
                                  <p className="text-[10px] font-semibold text-[#4B1E87] uppercase tracking-wide px-1">Backlog — click to add</p>
                                  {backlogTasks.length === 0 ? (
                                    <p className="text-xs text-muted-foreground px-1">Backlog is empty.</p>
                                  ) : backlogTasks.map(t => (
                                    <button
                                      key={t.id}
                                      onClick={() => handleAddBacklogTask(sprint.id, t.id)}
                                      disabled={saving}
                                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs text-slate-700 bg-white/80 border border-slate-100 hover:border-[#875AC3] hover:bg-white transition-colors"
                                    >
                                      <Plus className="w-3 h-3 text-[#4B1E87] shrink-0" />
                                      <span className="truncate">{t.title}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}

                {sprints.length === 0 && (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No sprints yet.{canManage ? ' Click "New Sprint" to create one.' : ''}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SprintModal
        open={showModal}
        sprint={editingSprint}
        tasks={tasks}
        members={members}
        onClose={() => { setShowModal(false); setEditingSprint(null); }}
        onSave={handleSave}
        loading={saving}
      />
    </>
  );
};

export default SprintPanel;
